/* eslint-disable no-prototype-builtins, react/jsx-no-target-blank, react/jsx-key */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import ContentfulWebFormStyles from './ContentfulWebForm.styled';

import Button from '@components/Shared/Buttons/Button';
import Typography from '@components/Shared/Typography/Typography';

import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import { sanitizeUrl } from 'pubweb-smokey/dist/utils/utils';
import {
  parseDisclaimerText,
  convertButtonColor,
} from 'pubweb-smokey/dist/utils/webFormHelpers';

import Form from '../Form/Form';
import TextField from '../TextField/TextField';
import CheckBox from '../CheckBox/CheckBox.js';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';

const ContentfulWebForm = ({ ...props }) => {
  const [formData, setFormData] = useState(
    typeof props.formData !== 'undefined' ? props.formData : {}
  );
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitError, setFormSubmitError] = useState(false);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [button1Disabled, setButton1Disabled] = useState(false);
  const [button2Disabled, setButton2Disabled] = useState(false);
  const [buttonClicked, setButtonClicked] = useState('1');
  const [thankYouVisible, setThankYouVisible] = useState(false);

  let formButtonText = `"${props.buttonText}"`;

  if (props.button2Text !== null && props.button2Text !== '') {
    formButtonText += ` OR "${props.button2Text}"`;
  }

  const customMessages = {
    firstNameMissing: 'First name is required.',
    lastNameMissing: 'Last name is required.',
    emailMissing: 'Email address is required.',
    zipMissing: 'Zip code is required.',
    phoneMissing: 'Phone number is required.',
    phoneInvalid:
      'Please provide a valid US phone number (including area code).',
  };

  //Update formData from props if we got account/location data.
  useEffect(() => {
    let newFormData = formData;

    if (typeof props.formData !== 'undefined' && props.formData !== null) {
      Object.entries(props.formData).forEach(([key, value]) => {
        if (
          !newFormData.hasOwnProperty(key) ||
          (newFormData.hasOwnProperty(key) && newFormData[key].trim() !== value)
        ) {
          newFormData[key] = value;
        }
      });

      setFormData(newFormData);
    }
  }, [formData, props.formData]);

  function handleFormChange(formData) {
    setFormData({ ...formData });
  }

  function handleValidation(formState) {
    const formErrors = runCommonValidation(formState, customMessages);

    if (
      formState.disclaimer &&
      formState.disclaimer.touched &&
      !formState.disclaimer.value
    ) {
      formErrors.disclaimer =
        'You must accept the disclaimer before submitting.';
      formErrors.hasErrors = true;
    }

    setFormErrors(formErrors);

    return formErrors.hasErrors;
  }

  function handleSubmit(formData) {
    if (submittingForm) {
      return;
    }

    setSubmittingForm(true);
    setFormSubmitError(false);
    setButton1Disabled(buttonClicked === '2' && true);
    setButton2Disabled(buttonClicked === '1' && true);

    const formSubmission = {
      ...formData,
      canEmail: true,
      dataExtension: props.dataExtension,
      originWebFormType: props.originWebFormType,
      originWebApp: props.originWebApp,
      originWebPageType: props.originWebPageType,
      originCampaign: props.originCampaign,
      originWebFormCategory:
        buttonClicked === '1'
          ? props.originWebFormCategory
          : props.originWebFormCategory2,
    };

    const getRedirectUrl = (url, zip) => {
      if (!url) {
        return '';
      }
      if (zip) {
        return sanitizeUrl(
          (url += url.includes('?') ? '&zip=' + zip : '?zip=' + zip)
        );
      } else {
        return sanitizeUrl(url);
      }
    };

    const redirectUrl = getRedirectUrl(props.redirectUrl, formData.zip);
    const redirectUrl2 = getRedirectUrl(props.redirectUrl2, formData.zip);

    props
      .submitForm(formSubmission)
      .then(() => {
        if (props.trackGAEvent) {
          // This is an old event name. I don't think it was being used, but keeping it for now. The GA4 form events will not use this.
          const eventName =
            buttonClicked === '1'
              ? props.customGAEventClass + ' ' + props.buttonText + '-submit'
              : props.customGAEventClass + ' ' + props.button2Text + '-submit';

          // Will use the buttonClicked when generating the GA4 event values.
          props.trackGAEvent(eventName, buttonClicked).then(() => {
            if (!props.stopRedirect) {
              window.location.href =
                buttonClicked === '1' ? redirectUrl : redirectUrl2;
            } else if (!props.thankYouInfo.isVisible) {
              setSubmittingForm(false);
              props.thankYouInfo.afterFormAction({
                email: formData?.email,
                firstName: formData?.firstName,
                lastName: formData?.lastName,
                phone: formData?.phone,
              });
            } else {
              setThankYouVisible(true);
            }
          });
        } else {
          if (!props.stopRedirect) {
            window.location.href =
              buttonClicked === '1' ? redirectUrl : redirectUrl2;
          } else if (!props.thankYouInfo.isVisible) {
            setSubmittingForm(false);
            props.thankYouInfo.afterFormAction({
              email: formData?.email,
              firstName: formData?.firstName,
              lastName: formData?.lastName,
              phone: formData?.phone,
            });
          } else {
            setThankYouVisible(true);
          }
        }
      })
      .catch(() => {
        setSubmittingForm(false);
        setFormSubmitError(true);
      });
  }

  return (
    <Typography>
      <ContentfulWebFormStyles className={props.formClassName}>
        {/* THANK YOU MESSAGE - ONLY SHOWS IF YOU PASS IN TRUE FOR stopRedirect */}
        {thankYouVisible && props.stopRedirect && (
          <div className="web-form-thank-you">
            {props.thankYouInfo.thankYouIncludeHeader && props.headerText && (
              <h1 className="form-header">{props.headerText}</h1>
            )}
            {props.thankYouInfo.thankYouIncludeHeader &&
              props.subheaderText && (
                <h2 className="form-subheader">{props.subheaderText}</h2>
              )}
            {props.thankYouInfo.thankYouIncludeHeader && props.formText && (
              <p className="form-text">{props.formText}</p>
            )}

            {props.thankYouInfo.thankYouImage &&
              props.thankYouInfo.thankYouImage}

            <p className="intro">
              {props.thankYouInfo.thankYouMessageText
                ? props.thankYouInfo.thankYouMessageText
                : 'Thank you for submitting the form.'}
            </p>

            <Button
              onClick={() => {
                setFormData({});
                setButton1Disabled(false);
                setButton2Disabled(false);
                setThankYouVisible(false);
                setSubmittingForm(false);

                if (
                  typeof props.thankYouInfo.thankYouButtonAction !== 'undefined'
                ) {
                  props.thankYouInfo.thankYouButtonAction();
                }
              }}
              automationId={
                props.thankYouInfo.thankYouButtonAutomationId
                  ? props.thankYouInfo.thankYouButtonAutomationId
                  : 'thank-you-button-click'
              }
            >
              {props.thankYouInfo.thankYouButtonText
                ? props.thankYouInfo.thankYouButtonText
                : 'Okay'}
            </Button>
          </div>
        )}

        {/* FORM - HIDE ALL OF THIS WHEN THE THANK YOU MESSAGE APPEARS */}
        {!thankYouVisible && (
          <div>
            {props.headerText && (
              <h1 className="form-header">{props.headerText}</h1>
            )}
            {props.subheaderText && (
              <h2 className="form-subheader">{props.subheaderText}</h2>
            )}

            {props.formText && <p className="form-text">{props.formText}</p>}

            {formSubmitError && (
              <span className="form-error">
                An error occurred while submitting the form. Please try again.
              </span>
            )}

            <Form
              onFormChange={handleFormChange}
              onFormValidate={handleValidation}
              onSubmit={handleSubmit}
            >
              <div className="field-row">
                {props.formContactFields.includes('First Name') && (
                  <div className="field-column">
                    <TextField
                      defaultValue={formData && formData.firstName}
                      id={`firstName${props.prefix}`}
                      automationId={`firstName${props.prefix}`}
                      name="firstName"
                      label="First Name"
                      errorMessage={formErrors && formErrors.firstName}
                    />
                  </div>
                )}

                {props.formContactFields.includes('Last Name') && (
                  <div className="field-column">
                    <TextField
                      defaultValue={formData && formData.lastName}
                      id={`lastName${props.prefix}`}
                      automationId={`lastName${props.prefix}`}
                      name="lastName"
                      label="Last Name"
                      errorMessage={formErrors && formErrors.lastName}
                    />
                  </div>
                )}
              </div>

              {props.formContactFields.includes('Email') && (
                <React.Fragment>
                  <div className="field-row">
                    <TextField
                      defaultValue={formData && formData.email}
                      id={`email${props.prefix}`}
                      automationId={`email${props.prefix}`}
                      name="email"
                      label="Email"
                      errorMessage={formErrors && formErrors.email}
                    />
                  </div>
                  {props.showEmailDisclaimer && (
                    <div className="field-row">
                      <CheckBox
                        label={props.emailDisclaimerText}
                        id={`contact${props.prefix}`}
                        automationId={`contact${props.prefix}`}
                        name="contact"
                      />
                    </div>
                  )}
                </React.Fragment>
              )}

              <div className="field-row">
                {props.formContactFields.includes('ZIP Code') && (
                  <div className="field-column">
                    <TextField
                      defaultValue={formData && formData.zip}
                      id={`zip${props.prefix}`}
                      automationId={`zip${props.prefix}`}
                      name="zip"
                      label="Zip Code"
                      errorMessage={formErrors && formErrors.zip}
                    />
                  </div>
                )}

                {props.formContactFields.includes('Phone Number') && (
                  <div className="field-column">
                    <TextField
                      defaultValue={formData && formData.phone}
                      id={`phone${props.prefix}`}
                      automationId={`phone${props.prefix}`}
                      name="phone"
                      label="Phone Number"
                      errorMessage={formErrors.phone}
                    />
                  </div>
                )}
              </div>

              {props.showDisclaimer ? (
                <div className="field-row">
                  <CheckBox
                    label={props.disclaimerText}
                    id={`disclaimer${props.prefix}`}
                    automationId={`disclaimer${props.prefix}`}
                    name="disclaimer"
                    className="disclaimer-checkbox"
                    defaultChecked={true}
                    value={formData && formData.disclaimer}
                  />

                  {formErrors.disclaimer ? (
                    <div
                      className="form-error disclaimer"
                      key="disclaimer-error"
                    >
                      {formErrors && formErrors.disclaimer}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {props.showWrittenConsentText &&
              props.formContactFields.includes('Phone Number') ? (
                <div className="field-row">
                  <CheckBox
                    label={props.writtenConsentText}
                    id={`writtenConsentTextMessage-${props.prefix}`}
                    automationId={`writtenConsentTextMessage${props.prefix}`}
                    name="writtenTextMessageConsent"
                    value={formData && formData.writtenTextMessageConsent}
                    className="written-consent-checkbox"
                  >
                    {props.writtenConsentText !== null ? (
                      props.writtenConsentText
                    ) : (
                      <div>
                        <p>Please text me (Optional)</p>
                      </div>
                    )}
                  </CheckBox>
                  {/* Going to be used in a future story inside contentful, waiting on design for confirmation. */}
                  {props.writtenConsentDisclaimerText !== null ? (
                    props.writtenConsentDisclaimerText
                  ) : (
                    <p className="sms-disclaimer">
                      By clicking the checkbox and then {formButtonText}, you
                      are signing this disclosure, in which you authorize
                      Clayton Homes and its service providers to send you
                      marketing text messages and telephone calls using
                      automated dialing technology to the mobile number you
                      provided in this form. Consent not required for purchase.
                      See our
                      <a
                        href="https://www.claytonhomes.com/account-terms-conditions/"
                        target="_blank"
                      >
                        Terms &amp; Conditions
                      </a>
                      , which includes opt-out instructions.
                    </p>
                  )}
                </div>
              ) : null}

              <div
                className={
                  props.button2Text
                    ? 'field-row buttons buttons-double'
                    : 'field-row buttons buttons-single'
                }
              >
                <div
                  className={
                    button1Disabled
                      ? 'button-disabled-container'
                      : 'button-container'
                  }
                >
                  <Button
                    automationId={
                      'js-' + props.originWebFormCategory + '-submit'
                    }
                    className={'js-' + props.originWebFormCategory + '-submit'}
                    primaryColor={convertButtonColor(props.buttonColor)}
                    disabled={(button1Disabled && true) || submittingForm}
                    fillContainer={true}
                    onClick={() => setButtonClicked('1')}
                  >
                    {submittingForm && !button1Disabled ? (
                      <LoadingIndicator color={props.buttonTextColor} />
                    ) : (
                      props.buttonText
                    )}
                  </Button>
                </div>
                {props.button2Text && (
                  <div
                    className={
                      button2Disabled
                        ? 'button-disabled-container'
                        : 'button-container'
                    }
                  >
                    <Button
                      automationId={
                        'js-' + props.originWebFormCategory2 + '-submit'
                      }
                      className={
                        'js-' + props.originWebFormCategory2 + '-submit'
                      }
                      primaryColor={convertButtonColor(props.buttonColor)}
                      buttonStyle={
                        props.button2Color === 'Inverse colors' && 'outlined'
                      }
                      disabled={(button2Disabled && true) || submittingForm}
                      fillContainer={true}
                      onClick={() => setButtonClicked('2')}
                    >
                      {submittingForm && !button2Disabled ? (
                        <LoadingIndicator color={props.button2TextColor} />
                      ) : (
                        props.button2Text
                      )}
                    </Button>
                  </div>
                )}

                {!props.showDisclaimer && props.disclaimerText ? (
                  <div className="disclaimer-text">
                    <p className="disclaimer-text">
                      {parseDisclaimerText(props.disclaimerText)}
                    </p>
                  </div>
                ) : null}

                {props.extData &&
                  props.extData[0] &&
                  Object.entries(props.extData[0]).map(([key, value]) => (
                    <input
                      type="hidden"
                      defaultValue={value}
                      name={key}
                      id={`${key}${props.prefix}`}
                    />
                  ))}
              </div>
            </Form>
          </div>
        )}
      </ContentfulWebFormStyles>
    </Typography>
  );
};

ContentfulWebForm.defaultProps = {
  prefix: '',
};

ContentfulWebForm.propTypes = {
  buttonColor: PropTypes.object,
  buttonText: PropTypes.string,
  buttonTextColor: PropTypes.string,
  button2Color: PropTypes.string,
  button2Text: PropTypes.string,
  button2TextColor: PropTypes.string,
  formClassName: PropTypes.string,
  headerIcon: PropTypes.string,
  headerColor: PropTypes.string,
  headerText: PropTypes.string,
  subheaderText: PropTypes.string,
  formData: PropTypes.object,
  formText: PropTypes.string,
  showDisclaimer: PropTypes.bool,
  disclaimerText: PropTypes.any,
  showEmailDisclaimer: PropTypes.bool,
  showWrittenConsentText: PropTypes.bool,
  writtenConsentText: PropTypes.object,
  writtenConsentDisclaimerText: PropTypes.object,
  emailDisclaimerText: PropTypes.string,
  redirectUrl: PropTypes.string,
  redirectUrl2: PropTypes.string,
  dataExtension: PropTypes.string,
  originWebFormType: PropTypes.string,
  originWebApp: PropTypes.string,
  originWebPageType: PropTypes.string,
  originCampaign: PropTypes.string,
  originWebFormCategory: PropTypes.string,
  originWebFormCategory2: PropTypes.string,
  submitForm: PropTypes.func,
  trackGAEvent: PropTypes.func,
  customGAEventClass: PropTypes.string,
  extData: PropTypes.array,
  stopRedirect: PropTypes.bool,
  prefix: PropTypes.string,
  thankYouInfo: PropTypes.shape({
    thankYouIncludeHeader: PropTypes.bool,
    thankYouMessageText: PropTypes.string,
    thankYouButtonText: PropTypes.string,
    thankYouButtonAction: PropTypes.func,
    thankYouButtonAutomationId: PropTypes.string,
    thankYouImage: PropTypes.string,
    isVisible: PropTypes.bool,
    afterFormAction: PropTypes.func,
  }),
  formContactFields: PropTypes.array,
};

export default ContentfulWebForm;
